import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form, InputGroup, OverlayTrigger } from 'react-bootstrap';
import { Tooltip } from 'react-bootstrap';

import { FieldLabel } from './../index';
import FieldTooltip from './field-tooltip';

import { FieldFeedback } from '.';

type Props = {
  readonly as?: React.ElementType<any>;
  readonly label: string | React.ReactNode;
  readonly pattern?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly type?: string;
  readonly tooltip?: string;
  readonly size?: string;
  readonly prefixLabel?: string;
  readonly prefixWidth?: string;
  readonly suffixLabel?: string;
  readonly suffixWidth?: string;
  readonly onChange?: React.ChangeEventHandler<HTMLInputElement>;
} & FieldAttributes<any>;

const FieldInput = ({
  as = 'div',
  name,
  label,
  helpText,
  pattern,
  disabled = false,
  placeholder = '',
  required = false,
  type = 'text',
  tooltip = 'Enter Password',
  hook,
  where,
  size,
  prefixLabel,
  prefixWidth = '7rem',
  suffixLabel,
  suffixWidth = '2rem',
  onChange,
}: Props) => {
  //
  //-------------------- Body -----------------------------------------------
  //
  const [{ value }, { touched, error, initialValue }] = useField<string>(name);
  const { setFieldValue, setFieldTouched, submitCount } = useFormikContext<any>();

  const setFieldValueAndTouched = useCallback(
    async (_value, touched: boolean) => {
      await setFieldValue(name, _value, true); //field,value,shouldValidate
      await setFieldTouched(name, touched); //field,touched,shouldValidate
      if (hook) {
        console.log('hook if');

        if (_value.length > 5) {
          console.log('value if');
          hook({ variables: { where: where(_value) } });
        }
      }
    },
    [hook, name, setFieldTouched, setFieldValue, where],
  );
  //
  //-------------------- Handlers -------------------------------------------
  //
  const handleChange = useCallback(
    (event) => {
      setFieldValueAndTouched(event.target.value, true);
      if (onChange) onChange(event);
    },
    [setFieldValueAndTouched],
  );

  const handleBlur = useCallback(async () => {
    await setFieldTouched(name, touched); //field,touched,shouldValidate
  }, [setFieldTouched, name, touched]);

  const changed = value !== initialValue;

  /** Πάντα string για εμφάνιση (και για nested paths / object errors από action validation) */
  const errorText =
    error == null
      ? ''
      : typeof error === 'string'
      ? error
      : typeof error === 'object' && !Array.isArray(error)
      ? Object.values(error)
          .flatMap((v) => (typeof v === 'string' ? [v] : []))
          .join(', ') || JSON.stringify(error)
      : String(error);

  // useWhyDidYouUpdate(`[FieldInput:${name}]`, {
  //   changed,
  //   disabled,
  //   error,
  //   handleBlur,
  //   handleChange,
  //   initialValue,
  //   label,
  //   name,
  //   placeholder,
  //   required,
  //   setFieldTouched,
  //   setFieldValue,
  //   touched,
  //   type,
  //   value,
  // });
  //
  //-------------------- Render -------------------------------------------
  //
  return useMemo(() => {
    return (
      <Form.Group as={as} controlId={name} className="mb-2">
        {label && (
          <FieldLabel
            size={size}
            text={label}
            helpText={helpText}
            changed={changed}
            required={required}
          />
        )}
        <OverlayTrigger
          delay={{ show: 1000, hide: 200 }}
          overlay={
            type === 'password' ? (
              <Tooltip id="password-tooltip">{tooltip}</Tooltip>
            ) : (
              <FieldTooltip text={name} value={value || ''} />
            )
          }
          placement="top"
        >
          <InputGroup>
            {prefixLabel && (
              <InputGroup.Text
                style={{
                  borderTopRightRadius: '0',
                  borderBottomRightRadius: '0',
                  width: prefixWidth,
                }}
              >
                {prefixLabel}
              </InputGroup.Text>
            )}
            <Form.Control
              style={{
                ...(required && !disabled ? { backgroundColor: 'lightYellow' } : {}),
                ...(prefixLabel ? { borderLeftWidth: '0' } : {}),
                ...(suffixLabel ? { borderRightWidth: '0' } : {}),
              }}
              // className="mb-2"  $$$$$$$ OXI $$$$$$$$
              // size={size}
              type={type}
              name={name}
              pattern={pattern}
              value={value || ''}
              placeholder={placeholder}
              isInvalid={(touched || submitCount > 0) && !!error}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {suffixLabel && (
              <InputGroup.Text
                style={{
                  borderTopLeftRadius: '0',
                  borderBottomLeftRadius: '0',
                  width: suffixWidth,
                }}
              >
                {suffixLabel}
              </InputGroup.Text>
            )}
          </InputGroup>
        </OverlayTrigger>
        {/* <FieldFeedback type="invalid" name={name} /> */}
        {(touched || submitCount > 0) && (
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        )}
      </Form.Group>
    );
  }, [
    as,
    name,
    label,
    changed,
    required,
    type,
    value,
    disabled,
    size,
    pattern,
    placeholder,
    touched,
    submitCount,
    error,
    errorText,
    handleChange,
    handleBlur,
  ]);
};

export default FieldInput;
